import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  // StatArrow removed - unused
} from "@chakra-ui/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import moment from "moment";
import api from "../../services/api";
import {
  ISO_FMT,
  // eslint-disable-next-line no-unused-vars
  MAX_DAYS_PER_CYCLE,
  getMaxDaysForEmployee,
  getIsAdminFromRecord,
  calcDaysToNextCycle,
  fmtBR,
  getAdmissionCycleWindow,
  overlapDaysInclusive,
  stripEmailDomain,
  toISO,
  isSprintCategory,
} from "./FullCalendarUtils";



function bestName(emp) {
  const name = emp?.name || emp?.NAME || "";
  const email = emp?.email || emp?.EMAIL || "";
  return String(name).trim() || stripEmailDomain(email) || "Usuário";
}

function bestEmail(emp) {
  return emp?.email || emp?.EMAIL || "";
}

function getEmpId(emp) {
  return String(emp?.id ?? emp?.ID ?? "");
}

function getEmpAdmissionISO(emp) {
  const raw = emp?.admissionDate || emp?.ADMISSION_DATE || emp?.admission_date || emp?.admission_date_AG_EMPLOYEES || null;
  return toISO(raw);
}

function getEmpNextCycleISO(emp, admissionISO) {
  const raw = emp?.nextLeaveCycleDate || emp?.NEXT_LEAVE_CYCLE_DATE || emp?.next_leave_cycle_date_AG_EMPLOYEES || null;
  const dbISO = toISO(raw);
  if (dbISO) return dbISO;

  // fallback: calcula pelo admission (se db ainda não preencheu)
  if (!admissionISO) return null;
  const todayISO = moment().format(ISO_FMT);
  const cycle = getAdmissionCycleWindow(admissionISO, todayISO);
  return cycle?.nextCycleStartISO || null;
}

function getEmpDaysToNextCycle(emp, nextCycleISO) {
  const raw = emp?.daysToNextLeaveCycle || emp?.DAYS_TO_NEXT_LEAVE_CYCLE || emp?.days_to_next_leave_cycle_AG_EMPLOYEES || null;
  const n = raw == null ? null : Number(raw);
  if (!Number.isNaN(n) && n != null) return n;
  return calcDaysToNextCycle(nextCycleISO, moment().format(ISO_FMT));
}

export default function AdminAbsencesReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") || "overview";
  // eslint-disable-next-line no-unused-vars
  const setTab = (t) => setSearchParams({ tab: t });

  const [year, setYear] = useState(moment().year());
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | READY | WAITING | OVER

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]); // eventos admin (range)
  const [rangeKey, setRangeKey] = useState(""); // evita re-fetch repetido

  const { isOpen: detailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const todayISO = useMemo(() => moment().format(ISO_FMT), []);

  // ✅ inicializa lista ao montar
  useEffect(() => {
    // Se não tiver funcionários carregados, busca
    if (employees.length === 0) {
      void refresh(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = useCallback(async () => {
    const r = await api.get("/calendar/employees");
    return Array.isArray(r.data) ? r.data : [];
  }, []);

  const fetchLeavesInRange = useCallback(async (startISO, endISO) => {
    const s = toISO(startISO);
    const e = toISO(endISO);
    const url = `/calendar/events/all?start=${encodeURIComponent(String(s))}&end=${encodeURIComponent(String(e))}`;
    const r = await api.get(url);
    const arr = Array.isArray(r.data) ? r.data : [];
    // Sprint é marcador global, não ausência: fora de todos os relatórios
    return arr.filter((ev) => !isSprintCategory(ev.category || ev.Category || ev.CATEGORY));
  }, []);

  const computeOverviewFetchRange = useCallback((emps) => {
    const refISO = moment().format(ISO_FMT);

    let minStart = null;
    let maxEnd = null;

    emps.forEach((emp) => {
      const admissionISO = getEmpAdmissionISO(emp);
      const nextCycleISO = getEmpNextCycleISO(emp, admissionISO);

      const cycle = getAdmissionCycleWindow(admissionISO, refISO);

      const cycleStartISO = cycle?.cycleStartISO || null;

      // cicloEnd: preferível = nextCycleStart - 1 dia (já vem do ciclo)
      const cycleEndISO = cycle?.cycleEndISO || null;

      if (cycleStartISO) {
        if (!minStart || cycleStartISO < minStart) minStart = cycleStartISO;
      }
      if (cycleEndISO) {
        if (!maxEnd || cycleEndISO > maxEnd) maxEnd = cycleEndISO;
      }

      // se DB trouxe nextCycleISO mas ciclo não, ainda garante
      if (nextCycleISO) {
        const end = moment(nextCycleISO, ISO_FMT, true).subtract(1, "day").format(ISO_FMT);
        if (!maxEnd || end > maxEnd) maxEnd = end;
      }
    });

    // fallback para não estourar query
    const fallbackStart = moment(refISO, ISO_FMT).subtract(1, "year").format(ISO_FMT);
    const fallbackEnd = moment(refISO, ISO_FMT).add(1, "year").format(ISO_FMT);

    return {
      startISO: minStart || fallbackStart,
      endISO: maxEnd || fallbackEnd,
    };
  }, []);

  const refresh = useCallback(async (force = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const emps = await fetchEmployees();
      setEmployees(emps);

      // overview: busca uma faixa “global” que cobre os ciclos atuais
      if (tab === "overview") {
        const { startISO, endISO } = computeOverviewFetchRange(emps);
        const key = `OV:${startISO}:${endISO}`;
        if (key !== rangeKey || force) {
          const arr = await fetchLeavesInRange(startISO, endISO);
          setLeaves(arr);
          setRangeKey(key);
        }
      }

      // year || analytics: busca o ano selecionado completo
      if (tab === "year" || tab === "analytics") {
        const startISO = `${year}-01-01`;
        const endISO = `${year}-12-31`;
        const key = `YR:${startISO}:${endISO}`;
        if (key !== rangeKey || force) {
          const arr = await fetchLeavesInRange(startISO, endISO);
          setLeaves(arr);
          setRangeKey(key);
        }
      }

      toast({ status: "success", title: "Relatório atualizado" });
    } catch (err) {
      console.error("AdminAbsencesReport.refresh failed", err);
      toast({
        status: "error",
        title: "Erro ao atualizar relatório",
        description: err?.response?.data?.message || err?.message || "Veja console",
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [tab, year, rangeKey, fetchEmployees, fetchLeavesInRange, computeOverviewFetchRange, toast]);

  // ✅ quando trocar aba/ano/param URL, refaz range
  useEffect(() => {
    void refresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, year]);

  const leavesByEmployee = useMemo(() => {
    const map = new Map(); // empId -> array
    (leaves || []).forEach((x) => {
      const empId = String(x.employeeId ?? x.EMPLOYEE_ID ?? "");
      if (!empId) return;
      if (!map.has(empId)) map.set(empId, []);
      map.get(empId).push(x);
    });

    // ordena por data início
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => {
        const as = toISO(a.start || a.START_DATE) || "";
        const bs = toISO(b.start || b.START_DATE) || "";
        if (as !== bs) return as.localeCompare(bs);
        const ae = toISO(a.end || a.END_DATE) || "";
        const be = toISO(b.end || b.END_DATE) || "";
        return ae.localeCompare(be);
      });
      map.set(k, arr);
    }

    return map;
  }, [leaves]);





  // =========================================================
  // ✅ REAL ANALYTICS CALCULATION
  // =========================================================
  const analyticsData = useMemo(() => {
    if (tab !== "analytics") return null;

    let totalDaysOff = 0;
    let totalLeaves = 0;
    const activeEmployeesVal = employees.length;

    // Inicializa meses
    const monthlyTrend = Array.from({ length: 12 }, (_, i) => ({
      name: moment().month(i).format("MMM"),
      total: 0,
      monthIndex: i
    }));

    // Dynamic Category Map
    const catMap = {};

    // Inicializa top absentees
    const employeeStats = {}; // empId -> { name, totalDays }

    const yearStart = moment(`${year}-01-01`);
    const yearEnd = moment(`${year}-12-31`);

    leaves.forEach(ev => {
      const start = moment(toISO(ev.start || ev.START_DATE));
      // normalize end: API is Exclusive, we need Inclusive for day-by-day stats
      let end = moment(toISO(ev.end || ev.END_DATE) || start);
      if (end.isAfter(start, "day")) {
        end.subtract(1, "day");
      }

      if (end.isBefore(yearStart) || start.isAfter(yearEnd)) return;

      const clampStart = moment.max(start, yearStart);
      const clampEnd = moment.min(end, yearEnd);

      // FIX: Use totalDays from DB if fully within range to avoid calculation errors
      // If event (start, end) is fully inside logic year, trust DB totalDays
      let days = 0;
      const isFullyInside = (start.isSameOrAfter(yearStart) && end.isSameOrBefore(yearEnd));
      const dbTotal = ev.totalDays || ev.TOTAL_DAYS || ev.total_days;

      if (isFullyInside && dbTotal) {
        days = Number(dbTotal);
      } else {
        days = clampEnd.diff(clampStart, "days") + 1;
      }

      if (days <= 0) return;

      totalDaysOff += days;
      totalLeaves += 1;

      // Monthly Trend
      const mIndex = start.month();
      if (monthlyTrend[mIndex]) {
        monthlyTrend[mIndex].total += days;
      }

      // Category (Dynamic)
      const rawCat = ev.category || ev.Category || ev.CATEGORY;
      const cat = rawCat ? String(rawCat).trim() : "Férias";

      if (!catMap[cat]) catMap[cat] = 0;
      catMap[cat] += days;

      // Employee Stats
      const empId = String(ev.employeeId || ev.EMPLOYEE_ID);
      if (!employeeStats[empId]) {
        const emp = employees.find(e => String(e.id) === empId);
        employeeStats[empId] = {
          name: bestName(emp),
          days: 0
        };
      }
      employeeStats[empId].days += days;
    });

    const averageDaysPerUser = activeEmployeesVal > 0
      ? (totalDaysOff / activeEmployeesVal).toFixed(1)
      : 0;

    // Palette
    const COLORS = ["#02ea75", "#3182ce", "#ecc94b", "#e53e3e", "#805ad5", "#d69e2e", "#38a169", "#d53f8c"];

    // Format By Category
    const byCategory = Object.entries(catMap).map(([name, value], idx) => ({
      name,
      value,
      color: COLORS[idx % COLORS.length]
    })).sort((a, b) => b.value - a.value);

    // Format Top Absentees
    const topAbsentees = Object.values(employeeStats)
      .sort((a, b) => b.days - a.days)
      .slice(0, 5)
      .map(x => ({
        ...x,
        status: x.days > 20 ? "Critico" : x.days > 10 ? "Atencao" : "Normal"
      }));

    return {
      summary: {
        totalDaysOff,
        totalLeaves,
        averageDaysPerUser,
        activeEmployees: activeEmployeesVal
      },
      monthlyTrend,
      byCategory,
      topAbsentees
    };
  }, [tab, leaves, employees, year]);


  const rowsOverview = useMemo(() => {
    const refISO = moment().format(ISO_FMT);

    return (employees || []).map((emp) => {
      const id = getEmpId(emp);
      const admissionISO = getEmpAdmissionISO(emp);
      const nextCycleISO = getEmpNextCycleISO(emp, admissionISO);
      const daysToNext = getEmpDaysToNextCycle(emp, nextCycleISO);

      const cycle = getAdmissionCycleWindow(admissionISO, refISO);

      // Check whitelist (Dynamic Search)
      const keys = Object.keys(emp || {});
      const wlKey = keys.find((k) => k.toLowerCase().includes("whitelist"));
      const wlVal = wlKey ? emp[wlKey] : null;

      const isWhitelisted =
        String(wlVal) === "1" || String(wlVal) === "true" || wlVal === true;

      if (!isWhitelisted) return null; // Filter out non-whitelisted

      const empLeaves = leavesByEmployee.get(String(id)) || [];

      let used = 0;
      empLeaves.forEach((ev) => {
        const s = toISO(ev.start || ev.START_DATE);
        // API End Exclusive -> Inclusive for overlap check
        let e = toISO(ev.end || ev.END_DATE) || s;
        if (e !== s) {
          e = moment(e).subtract(1, "day").format(ISO_FMT);
        }

        if (!s || !e) return;

        // ✅ Only count "Férias" (robust check)
        const rawCat = ev.category || ev.Category || "Férias";
        const cat = String(rawCat).toLowerCase().trim();
        // Check for "férias" or "ferias"
        if (!cat.includes("férias") && !cat.includes("ferias")) return;

        // FIX: Use totalDays if fully within cycle to avoid off-by-one errors
        const dbTotal = ev.totalDays || ev.TOTAL_DAYS || ev.total_days;
        const startM = moment(s);
        const endM = moment(e); // inclusive
        const cycleStartM = moment(cycle.cycleStartISO);
        const cycleEndM = moment(cycle.cycleEndISO);

        const isFullyInside = startM.isSameOrAfter(cycleStartM) && endM.isSameOrBefore(cycleEndM);

        if (isFullyInside && dbTotal) {
          used += Number(dbTotal);
        } else {
          used += overlapDaysInclusive(s, e, cycle.cycleStartISO, cycle.cycleEndISO);
        }
      });

      const maxDays = getMaxDaysForEmployee(emp);
      const remaining = Math.max(0, maxDays - used);

      // último OFF e próximo OFF
      const today = moment(refISO, ISO_FMT, true).startOf("day");
      let lastOff = null;
      let nextOff = null;

      for (const ev of empLeaves) {
        const s = moment(toISO(ev.start || ev.START_DATE) || "", ISO_FMT, true).startOf("day");
        const e = moment(toISO(ev.end || ev.END_DATE) || "", ISO_FMT, true).startOf("day");
        if (!s.isValid() || !e.isValid()) continue;

        if (e.isBefore(today, "day")) {
          lastOff = ev;
        } else if (s.isSameOrAfter(today, "day") && !nextOff) {
          nextOff = ev;
        }
      }

      const status =
        used > maxDays
          ? "OVER"
          : daysToNext != null && daysToNext <= 0
            ? "READY"
            : "WAITING";

      return {
        id,
        name: bestName(emp),
        email: bestEmail(emp),
        isAdmin: getIsAdminFromRecord(emp),
        isComercial: (() => {
          const cf = emp?.is_comercial_AG_EMPLOYEES ?? emp?.IS_COMERCIAL_AG_EMPLOYEES ?? emp?.isComercial ?? emp?.IS_COMERCIAL ?? emp?.is_comercial ?? 0;
          return cf === 1 || cf === true || cf === "1";
        })(),
        maxDays,
        admissionISO,
        cycleStartISO: cycle.cycleStartISO,
        cycleEndISO: cycle.cycleEndISO,
        nextCycleISO: nextCycleISO || cycle.nextCycleStartISO || null,
        daysToNext,
        used,
        remaining,
        status,
        lastOff,
        nextOff,
        isWhitelisted // keep for debugging if needed
      };
    }).filter(Boolean); // Remove nulls
  }, [employees, leavesByEmployee]);

  const statusCounts = useMemo(() => {
    const c = { ALL: 0, READY: 0, WAITING: 0, OVER: 0 };
    rowsOverview.forEach((r) => {
      c.ALL += 1;
      c[r.status] += 1;
    });
    return c;
  }, [rowsOverview]);

  const filteredOverview = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();

    return rowsOverview
      .filter((r) => {
        if (statusFilter !== "ALL" && r.status !== statusFilter) return false;

        if (!q) return true;

        const bag = `${r.name} ${r.email} ${r.id}`.toLowerCase();
        return bag.includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rowsOverview, query, statusFilter]);

  const rowsYear = useMemo(() => {
    const arr = Array.isArray(leaves) ? leaves : [];
    const out = arr.map((x) => {
      const id = String(x.id ?? x.ID ?? "");
      const empId = String(x.employeeId ?? x.EMPLOYEE_ID ?? "");
      const name = x.employeeName || x.EMPLOYEE_NAME || "";
      const email = x.employeeEmail || x.EMPLOYEE_EMAIL || "";
      const startISO = toISO(x.start || x.START_DATE);
      const endISO = toISO(x.end || x.END_DATE) || startISO;
      const category = x.category || x.Category || x.CATEGORY || null;
      const comment = x.comment || x.COMMENT || x.title || "OFF";

      // Fix Dates & Duration
      // API uses exclusive end (standard fullcalendar).
      // We want to display INCLUSIVE end (end - 1 day).
      const mStart = moment(startISO);
      const mEnd = moment(endISO);

      const endInclusiveISO = mEnd.clone().subtract(1, 'day').format(ISO_FMT);

      // Duration: priority DB totalDays, else end - start (exclusive diff = days count)
      // Force calc to fix "1 day more" issue reported by user if DB totalDays is wrong
      // Duration: priority DB totalDays, else end - start (exclusive diff = days count)
      let duration = 0;
      const dbTotal = x.totalDays || x.TOTAL_DAYS || x.total_days;

      if (dbTotal) {
        duration = Number(dbTotal);
      } else {
        // Fallback to calculation
        let d = mEnd.diff(mStart, 'days');
        if (d < 1) d = 1;
        duration = d;
      }

      return {
        id,
        empId,
        user: String(name).trim() || stripEmailDomain(email) || "Usuário",
        email,
        startISO,
        endISO, // Raw exclusive
        endInclusiveISO, // For display
        duration, // Total days
        category,
        comment,
      };
    });

    out.sort((a, b) => (a.startISO || "").localeCompare(b.startISO || ""));
    return out;
  }, [leaves]);

  const filteredYear = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return rowsYear;
    return rowsYear.filter((r) => {
      const bag = `${r.user} ${r.email} ${r.empId} ${r.comment}`.toLowerCase();
      return bag.includes(q);
    });
  }, [rowsYear, query]);

  const selectedRow = useMemo(() => {
    if (!selectedEmployeeId) return null;
    return rowsOverview.find((r) => String(r.id) === String(selectedEmployeeId)) || null;
  }, [rowsOverview, selectedEmployeeId]);

  const openDetails = (empId) => {
    setSelectedEmployeeId(String(empId));
    onDetailsOpen();
  };

  const jumpToNext = (row) => {
    const next = row?.nextOff;
    // eslint-disable-next-line no-unused-vars
    const s = toISO(next?.start || next?.START_DATE) || row?.nextCycleISO || todayISO;
    // ✅ Pass date to calendar via query param
    navigate(`/calendar?date=${s}`);
    toast({ title: "Navegando para calendário...", status: "info", duration: 2000 });
  };

  const statusBadge = (status, daysToNext) => {
    if (status === "OVER") return <Badge colorScheme="red">Estourou limite</Badge>;
    if (status === "READY") return <Badge colorScheme="green">Liberado</Badge>;
    return <Badge colorScheme="yellow">Aguardando</Badge>;
  };

  const TabButton = ({ id, label, count }) => (
    <Button
      size="sm"
      variant={statusFilter === id ? "solid" : "outline"}
      colorScheme={statusFilter === id ? "green" : "gray"}
      onClick={() => setStatusFilter(id)}
    >
      {label} <Badge ml={2} colorScheme="blackAlpha">{count}</Badge>
    </Button>
  );

  return (
    <Box p={6} minH="calc(100vh - 80px)">
      <Flex align="center" gap={3} mb={6}>
        <VStack align="start" spacing={0}>
          <Text fontSize="2xl" fontWeight={900} color="textPrimary">
            Relatório — Funcionários
          </Text>
          <Text fontSize="md" color="textSecondary" fontWeight={500}>
            Acompanhamento das férias
          </Text>
        </VStack>
        <Spacer />
      </Flex>

      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Input
          maxW="420px"
          placeholder="Buscar por nome, email ou ID..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          bg="surfaceRaised"
          border="1px solid" borderColor="borderPrimary"
          color="textPrimary"
        />

        {tab === "overview" ? (
          <HStack spacing={2} flexWrap="wrap">
            <TabButton id="ALL" label="Todos" count={statusCounts.ALL} />
            <TabButton id="READY" label="Liberado" count={statusCounts.READY} />
            <TabButton id="WAITING" label="Aguardando" count={statusCounts.WAITING} />
            <TabButton id="OVER" label="Estourou limite" count={statusCounts.OVER} />
          </HStack>
        ) : tab === "analytics" ? (
          <Text color="textSecondary" fontSize="sm" fontWeight={500}>

          </Text>
        ) : (
          <HStack spacing={2}>
            <Text color="textSecondary" fontSize="sm" fontWeight={700}>
              Ano:
            </Text>
            <Select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              size="sm"
              w="140px"
              bg="surfaceRaised"
              color="textPrimary"
              border="1px solid" borderColor="borderPrimary"
            >
              {Array.from({ length: 7 }).map((_, i) => {
                const y = moment().year() - 3 + i;
                return (
                  <option key={y} value={y} style={{ background: "#111" }}>
                    {y}
                  </option>
                );
              })}
            </Select>
          </HStack>
        )}

        <Spacer />

        <Spacer />

        {/* Botão Atualizar removido - Auto-refresh visual via tabela */}
      </Flex>

      <Box position="relative">
        {loading && (
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="overlayBg"
            zIndex={10}
            align="center"
            justify="center"
            borderRadius="xl"
            backdropFilter="blur(2px)"
          >
            <VStack spacing={4}>
              <Spinner size="xl" color="green.400" thickness="4px" />
              <Text fontWeight={700} color="textPrimary">Atualizando...</Text>
            </VStack>
          </Flex>
        )}

        <Box border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
          <Flex px={4} py={3} align="center" justify="space-between">
            <Text color="textPrimary" fontWeight={900}>
              {tab === "overview"
                ? "Resumo por funcionário"
                : tab === "analytics"
                  ? "Dashboard Gerencial"
                  : "Ausências registradas no ano"}
            </Text>
            <Text color="textSecondary" fontSize="sm">
              {tab === "overview"
                ? `${filteredOverview.length} funcionário(s)`
                : tab === "analytics"
                  ? "Ano atual"
                  : `${filteredYear.length} registro(s)`}
            </Text>
          </Flex>

          <Divider borderColor="borderPrimary" />

          {tab === "overview" ? (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th color="textSecondary">Funcionário</Th>
                  <Th color="textSecondary">Admissão</Th>
                  <Th color="textSecondary">Próxima virada</Th>
                  <Th color="textSecondary" isNumeric>
                    Dias p/ liberar
                  </Th>
                  <Th color="textSecondary" isNumeric>
                    Usados
                  </Th>
                  <Th color="textSecondary" isNumeric>
                    Saldo
                  </Th>
                  <Th color="textSecondary">Status</Th>
                  <Th color="textSecondary">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredOverview.map((r) => (
                  <Tr key={r.id} _hover={{ bg: "surfaceHover" }}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <HStack spacing={2}>
                          <Text color="textPrimary" fontWeight={900}>
                            {r.name}
                          </Text>
                          {r.isAdmin ? <Badge colorScheme="purple">Admin</Badge> : null}
                          {r.isComercial && <Badge colorScheme="purple">Comercial</Badge>}
                        </HStack>
                        <Text color="textSecondary" fontSize="xs">
                          {r.email || "—"}
                        </Text>
                      </VStack>
                    </Td>
                    <Td color="textPrimary" fontWeight={700}>
                      {fmtBR(r.admissionISO)}
                    </Td>
                    <Td color="textPrimary" fontWeight={800}>
                      {fmtBR(r.nextCycleISO)}
                    </Td>
                    <Td isNumeric color="textPrimary" fontWeight={900}>
                      {r.daysToNext == null ? "—" : r.daysToNext}
                    </Td>
                    <Td isNumeric color="textPrimary" fontWeight={900}>
                      {r.used} / {r.maxDays}
                    </Td>
                    <Td isNumeric color="textPrimary" fontWeight={900}>
                      {r.remaining}
                    </Td>
                    <Td>{statusBadge(r.status, r.daysToNext)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button size="xs" colorScheme="green" variant="outline" onClick={() => openDetails(r.id)}>
                          Detalhes
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}

                {!filteredOverview.length && (
                  <Tr>
                    <Td colSpan={8}>
                      <Text color="textSecondary" px={2} py={6}>
                        Nenhum resultado.
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          ) : tab === "analytics" ? (
            <Box p={5}>
              {/* Se não houve cálculo (ex: loading ou erro), fallback */}
              {!analyticsData ? (
                <Flex align="center" justify="center" h="400px">
                  <Spinner color="green.400" size="xl" />
                </Flex>
              ) : (
                <>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5} mb={8}>
                    <Box bg="surfaceRaised" p={4} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Stat>
                        <StatLabel color="textSecondary">Total Ausências (Ano)</StatLabel>
                        <StatNumber color="textPrimary" fontSize="3xl">
                          {analyticsData.summary.totalDaysOff} <Text as="span" fontSize="sm" color="textMuted">dias</Text>
                        </StatNumber>
                      </Stat>
                    </Box>
                    <Box bg="surfaceRaised" p={4} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Stat>
                        <StatLabel color="textSecondary">Total Eventos</StatLabel>
                        <StatNumber color="textPrimary" fontSize="3xl">
                          {analyticsData.summary.totalLeaves}
                        </StatNumber>
                      </Stat>
                    </Box>
                    <Box bg="surfaceRaised" p={4} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Stat>
                        <StatLabel color="textSecondary">Média por Colaborador</StatLabel>
                        <StatNumber color="textPrimary" fontSize="3xl">
                          {analyticsData.summary.averageDaysPerUser}
                        </StatNumber>
                        <StatHelpText color="textMuted">dias / ano</StatHelpText>
                      </Stat>
                    </Box>
                    <Box bg="surfaceRaised" p={4} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Stat>
                        <StatLabel color="textSecondary">Colaboradores Ativos</StatLabel>
                        <StatNumber color="textPrimary" fontSize="3xl">
                          {analyticsData.summary.activeEmployees}
                        </StatNumber>
                      </Stat>
                    </Box>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={8}>
                    <Box bg="surfaceRaised" p={5} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Text fontWeight={800} color="textPrimary" mb={4}>
                        Tendência Anual de Ausências
                      </Text>
                      <Box h="300px" w="100%">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={analyticsData.monthlyTrend}>
                            <defs>
                              <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#02ea75" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#02ea75" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.25)" vertical={false} />
                            <XAxis dataKey="name" stroke="#718096" tick={{ fill: "#718096" }} tickLine={false} axisLine={false} />
                            <YAxis stroke="#718096" tick={{ fill: "#718096" }} tickLine={false} axisLine={false} />
                            <Tooltip
                              contentStyle={{ backgroundColor: "var(--chakra-colors-surface)", border: "1px solid var(--chakra-colors-borderPrimary)", borderRadius: "8px" }}
                              itemStyle={{ color: "var(--chakra-colors-textPrimary)" }}
                            />
                            <Area type="monotone" dataKey="total" stroke="#02ea75" strokeWidth={3} fillOpacity={1} fill="url(#colorTotal)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="surfaceRaised" p={5} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                      <Text fontWeight={800} color="textPrimary" mb={4}>
                        Categorias de Ausência
                      </Text>
                      <Flex h="300px" align="center" justify="center">
                        {analyticsData.byCategory.length === 0 ? (
                          <Text color="textMuted">Sem dados para este ano</Text>
                        ) : (
                          <Box h="100%" w="100%">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={analyticsData.byCategory}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={90}
                                  paddingAngle={5}
                                  dataKey="value"
                                >
                                  {analyticsData.byCategory.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ backgroundColor: "var(--chakra-colors-surface)", border: "1px solid var(--chakra-colors-borderPrimary)", borderRadius: "8px" }}
                                  itemStyle={{ color: "var(--chakra-colors-textPrimary)" }}
                                />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </Flex>
                    </Box>
                  </SimpleGrid>

                  <Box bg="surfaceRaised" p={5} borderRadius="xl" border="1px solid" borderColor="borderPrimary">
                    <Text fontWeight={800} color="textPrimary" mb={4}>
                      Top 5 - Colaboradores com mais ausências
                    </Text>
                    <Table size="sm" variant="unstyled">
                      <Thead>
                        <Tr borderBottom="1px solid" borderColor="borderPrimary">
                          <Th color="textMuted">Nome</Th>
                          <Th color="textMuted" isNumeric>Dias Totais</Th>
                          <Th color="textMuted">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsData.topAbsentees.map((p, i) => (
                          <Tr key={i} _hover={{ bg: "surfaceHover" }}>
                            <Td color="textPrimary" fontWeight={600}>{p.name}</Td>
                            <Td color="textPrimary" isNumeric fontWeight={700}>{p.days}</Td>
                            <Td>
                              <Badge colorScheme={p.status === "Critico" ? "red" : p.status === "Atencao" ? "yellow" : "green"}>
                                {p.status}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                        {analyticsData.topAbsentees.length === 0 && (
                          <Tr>
                            <Td colSpan={3} color="textMuted" py={4}>Sem dados registrados</Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th color="textSecondary">Usuário</Th>
                  <Th color="textSecondary">Início</Th>
                  <Th color="textSecondary">Fim</Th>
                  <Th color="textSecondary" isNumeric>Dias</Th>
                  <Th color="textSecondary">Categoria</Th>
                  <Th color="textSecondary">Comentário</Th>
                  <Th color="textSecondary">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredYear.map((r) => (
                  <Tr key={r.id} _hover={{ bg: "surfaceHover" }}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text color="textPrimary" fontWeight={900}>
                          {r.user}
                        </Text>
                        <Text color="textSecondary" fontSize="xs">
                          {r.email || `ID: ${r.empId}`}
                        </Text>
                      </VStack>
                    </Td>
                    <Td color="textPrimary" fontWeight={800}>
                      {fmtBR(r.startISO)}
                    </Td>
                    <Td color="textPrimary" fontWeight={800}>
                      {fmtBR(r.endInclusiveISO)}
                    </Td>
                    <Td color="textPrimary" fontWeight={800} isNumeric>
                      {r.duration}
                    </Td>
                    <Td color="textSecondary">
                      {r.category || "—"}
                    </Td>
                    <Td color="textSecondary" maxW="320px">
                      {r.comment || "OFF"}
                    </Td>
                    <Td>
                      <Button
                        size="xs"
                        colorScheme="green"
                        onClick={() => {
                          navigate(`/calendar?date=${r.startISO}`);
                          toast({ title: "Navegando para calendário...", status: "info", duration: 1500 });
                        }}
                      >
                        Ver no calendário
                      </Button>
                    </Td>
                  </Tr>
                ))}

                {!filteredYear.length && (
                  <Tr>
                    <Td colSpan={5}>
                      <Text color="textSecondary" px={2} py={6}>
                        Nenhum resultado.
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>


      {/* ✅ Modal Detalhes do funcionário */}
      <Modal
        isOpen={detailsOpen}
        onClose={() => {
          onDetailsClose();
          setSelectedEmployeeId(null);
        }}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent bg="surface" color="textPrimary" borderRadius="2xl" overflow="hidden" border="1px solid" borderColor="borderPrimary">
          <ModalHeader color="textPrimary">Detalhes do funcionário</ModalHeader>

          <ModalBody>
            {!selectedRow ? (
              <Text color="textSecondary">Selecione um funcionário.</Text>
            ) : (
              <>
                <Box>
                  <Text color="textSecondary" fontSize="sm" mb={1}>
                    Funcionário
                  </Text>
                  <HStack spacing={2}>
                    <Text color="textPrimary" fontWeight={900} fontSize="lg">
                      {selectedRow.name}
                    </Text>
                    {selectedRow.isAdmin ? <Badge colorScheme="purple">Admin</Badge> : null}
                    {selectedRow.isComercial ? <Badge colorScheme="purple">Comercial</Badge> : null}
                  </HStack>
                  <Text color="textSecondary" fontSize="sm">
                    {selectedRow.email || "—"}
                  </Text>
                </Box>

                <Divider my={4} borderColor="borderPrimary" />

                {/* ✅ Color Picker UI */}


                <Divider my={4} borderColor="borderPrimary" />

                <VStack align="stretch" spacing={3}>
                  <Flex justify="space-between">
                    <Text color="textSecondary">Admissão</Text>
                    <Text color="textPrimary" fontWeight={900}>
                      {fmtBR(selectedRow.admissionISO)}
                    </Text>
                  </Flex>


                  <Flex justify="space-between">
                    <Text color="textSecondary">Próxima virada</Text>
                    <Text color="textPrimary" fontWeight={900}>
                      {fmtBR(selectedRow.nextCycleISO)}
                    </Text>
                  </Flex>

                  <Flex justify="space-between">
                    <Text color="textSecondary">Dias até liberar</Text>
                    <Text color="textPrimary" fontWeight={900}>
                      {selectedRow.daysToNext == null ? "—" : `${selectedRow.daysToNext} dias`}
                    </Text>
                  </Flex>

                  <Divider borderColor="borderPrimary" />

                  <Flex justify="space-between">
                    <Text color="textSecondary">Dias usados das férias</Text>
                    <Text color="textPrimary" fontWeight={900}>
                      {selectedRow.used} / {selectedRow.maxDays}
                    </Text>
                  </Flex>

                  <Flex justify="space-between">
                    <Text color="textSecondary">Dias restantes</Text>
                    <Text color="textPrimary" fontWeight={900}>
                      {selectedRow.remaining}
                    </Text>
                  </Flex>
                </VStack>

                <Divider my={4} borderColor="borderPrimary" />



                <Box mt={3}>
                  <Text color="textSecondary" fontSize="sm" mb={1}>
                    Próximo OFF agendado
                  </Text>

                  {selectedRow.nextOff ? (
                    <>
                      <Text color="textPrimary" fontWeight={900}>
                        {fmtBR(toISO(selectedRow.nextOff.start || selectedRow.nextOff.START_DATE))} →{" "}
                        {fmtBR(toISO(selectedRow.nextOff.end || selectedRow.nextOff.END_DATE))}
                      </Text>
                      <Text color="textSecondary" fontSize="sm">
                        {selectedRow.nextOff.comment ||
                          selectedRow.nextOff.COMMENT ||
                          selectedRow.nextOff.title ||
                          "OFF"}
                      </Text>

                      <Button
                        mt={3}
                        colorScheme="green"
                        onClick={() => jumpToNext(selectedRow)}
                        isDisabled={!selectedRow.nextOff}
                      >
                        Ver próximo no calendário
                      </Button>
                    </>
                  ) : (
                    <>
                      <Text color="textPrimary" fontWeight={700}>
                        —
                      </Text>
                      <Text color="textSecondary" fontSize="sm">
                        Nenhum período futuro encontrado nesta faixa (use “Atualizar” se acabou de criar algo).
                      </Text>
                    </>
                  )}
                </Box>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="green"
              onClick={() => {
                onDetailsClose();
                setSelectedEmployeeId(null);
              }}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box >
  );
}
